import {
    AfterViewInit,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    EventEmitter,
    inject,
    input,
    signal,
    viewChild,
    ChangeDetectionStrategy,
} from "@angular/core"
import paper from "paper"
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop"
import {BehaviorSubject, Observable, Subject, throttleTime} from "rxjs"
import {CanvasNavigation} from "@common/helpers/canvas/canvas-navigation"
import {Matrix3x2, Size2Like, Vector2Like} from "@cm/math"
import {Hotkeys} from "@common/services/hotkeys/hotkeys.service"
import {CanvasToolboxRootItem} from "@common/helpers/canvas/canvas-toolbox/canvas-toolbox-root-item"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-canvas",
    imports: [],
    templateUrl: "./canvas.component.html",
    styleUrl: "./canvas.component.scss",
})
export class CanvasComponent implements AfterViewInit {
    readonly hotkeys = inject(Hotkeys)

    readonly redraw = new EventEmitter<void>()

    readonly $canvasBounds = signal<Size2Like>({width: 0, height: 0}, {equal: (a, b) => a.width === b.width && a.height === b.height})
    readonly $pixelsPerCm = input<number>(10, {alias: "pixelsPerCm"})
    readonly $viewTransform = computed(() => this.$data()?.navigation.$viewMatrix() ?? Matrix3x2.identity())
    readonly $physicalZoomFactor = computed(() => this.$data()?.navigation.$physicalZoomFactor() ?? 1)
    readonly $viewZoomLevel = computed(() => this.$data()?.navigation.$zoomLevel() ?? 1)
    readonly $viewOffsetPx = signal<Vector2Like>({x: 0, y: 0}, {equal: (a, b) => a.x === b.x && a.y === b.y})
    readonly $cursorPositionPx = signal<Vector2Like>({x: 0, y: 0}, {equal: (a, b) => a.x === b.x && a.y === b.y})

    readonly $toolbox = signal<CanvasToolboxRootItem | null>(null)

    readonly canvasBoundsChanged$ = toObservable(this.$canvasBounds)
    private readonly _viewTransformSubject = new BehaviorSubject<Matrix3x2>(Matrix3x2.identity())
    readonly viewTransformChanged$: Observable<Matrix3x2> = this._viewTransformSubject.asObservable()
    readonly pixelsPerCmChanged$ = toObservable(this.$pixelsPerCm)
    readonly toolboxChanged$ = toObservable(this.$toolbox)

    constructor() {
        this.hotkeys
            .addShortcut("Space")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                const physicalZoomLevel: number = this.$physicalZoomFactor()
                // This is needed because of the rounding errors, since the zoom level at 100% is often 0.99999999 instead of 1.
                const epsilon: number = Math.abs(physicalZoomLevel - 1)
                if (epsilon < 0.01) {
                    this.zoomToFitCanvasBounds(1)
                } else {
                    this.zoomToPhysicalPixels()
                }
            })
    }

    ngAfterViewInit() {
        const toolCanvas = this.$toolCanvasElementRef().nativeElement

        const paperScope = new paper.PaperScope()
        paperScope.setup(toolCanvas)
        const paperProject = paperScope.project
        const paperLayer = paperProject.activeLayer
        paperLayer.name = "PaperLayer"
        paperScope.activate()

        const navigation = new CanvasNavigation(toolCanvas, paperProject)
        navigation.viewChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((viewMatrix) => {
            this.$viewOffsetPx.set({x: -viewMatrix.elements[4], y: -viewMatrix.elements[5]})
            this._viewTransformSubject.next(viewMatrix)
            this.requestRedraw()
        })
        navigation.cursorPosChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => this.$cursorPositionPx.set(value))
        navigation.zoomTo(1)
        navigation.physicalInfo = {
            pixelsPerCm: this.$pixelsPerCm(),
            originXCm: 0,
            originYCm: 0,
        }

        this.$data.set({
            paperScope,
            paperProject,
            paperLayer,
            navigation,
        })

        this.pixelsPerCmChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            navigation.physicalInfo = {
                pixelsPerCm: this.$pixelsPerCm(),
                originXCm: 0,
                originYCm: 0,
            }
        })
        this.canvasBoundsChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((canvasBounds) => {
            this.data.navigation.$canvasBounds.set(canvasBounds)
        })
        this.toolboxChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((toolbox) => {
            this.data.navigation.toolboxRootItem = toolbox
        })
        this.redrawRequested$
            .pipe(takeUntilDestroyed(this.destroyRef), throttleTime(10, undefined, {leading: true, trailing: true}))
            .subscribe(() => this.redraw.next())
    }

    get htmlCanvasElement(): HTMLCanvasElement {
        return this.$mainCanvasElementRef().nativeElement
    }

    // TODO on macOS the reported resolution is sometimes altered by the OS so that the devicePixelRatio is integer to avoid blurry rendering
    // TODO though this can cause excessive over-sampling resolution so the max resolution is limited to 4K (in webgl2-canvas.component.ts)
    // TODO however when mapping the view coordinates through the inverse view transform, it is still too large i think - to be further investigated !
    get devicePixelRatio(): number {
        return devicePixelRatio
    }

    zoomToPhysicalPixels() {
        this.data.navigation.physicalZoomTo(1)
    }

    zoomToFitCanvasBounds(adjustFactor: number) {
        this.data.navigation.zoomToFitCanvasBounds(adjustFactor)
    }

    beginPaperCreation(): void {
        this.data.paperScope.activate()
        this.data.paperLayer.activate()
    }

    requestRedraw(): void {
        this.redrawRequested$.next()
    }

    containsPoint(point: Vector2Like): boolean {
        const bounds = this.$canvasBounds()
        return point.x >= 0 && point.y >= 0 && point.x < bounds.width && point.y < bounds.height
    }

    private get data(): Data {
        const data = this.$data()
        if (!data) {
            throw new Error("Canvas data is not initialized yet")
        }
        return data
    }

    private readonly destroyRef = inject(DestroyRef)
    private readonly $toolCanvasElementRef = viewChild.required<ElementRef>("toolCanvas")
    private readonly $mainCanvasElementRef = viewChild.required<ElementRef>("mainCanvas")

    private redrawRequested$ = new Subject<void>()

    private readonly $data = signal<Data | undefined>(undefined)
}

type Data = {
    paperScope: paper.PaperScope
    paperProject: paper.Project
    paperLayer: paper.Layer
    navigation: CanvasNavigation
}
